import { message } from 'antd';
import { AxiosInstance } from 'axios';

import User from '@/model/General/User';
import { PackageMessage } from '@/model/Logistic/PackageMessage';
import { PACKAGE_STATES } from '@/model/Logistic/PackageStates';
import { Shipment } from '@/model/Logistic/Shipment/Shipment';
import { ShippingLabel } from '@/model/Logistic/ShippingLabel/ShippingLabel';

import ShipmentApi from './ShipmentApi';

class ShipmentRepository {
  shipmentApi: ShipmentApi;

  constructor(axiosInstance: AxiosInstance) {
    this.shipmentApi = new ShipmentApi(axiosInstance);
  }

  printShippingLabel(shipmentId: number) {
    return this.shipmentApi
      .printShippingLabel(shipmentId)
      .then(() => message.success('Etiqueta de envío generada correctamente'))
      .catch(() => message.error('Error al generar la etiqueta de envío'));
  }

  saveNewAddress = async shipmentData => {
    const shippingLabel: ShippingLabel = {
      address: shipmentData.receiver_address,
      email: shipmentData.receiver_email,
      phone_number: shipmentData.receiver_phone,
      localidad: shipmentData.receiver_localidad,
      postal_code: shipmentData.receiver_postal_code,
      company: shipmentData.company,
      contact: shipmentData.receiver_name,

      description: `${shipmentData.receiver_address} - ${shipmentData.receiver_name} - ${shipmentData.receiver_phone} - ${shipmentData.receiver_email}`,
    };

    try {
      const response = await this.shipmentApi.createAddress(shippingLabel);

      return response.data;
    } catch (error) {
      console.error('Error saving address:', error);
      throw error;
    }
  };

  updateShipmentState = async (shipmentId: number, newState: PACKAGE_STATES) => {
    try {
      const response = await this.shipmentApi.updateShipmentState(shipmentId, newState);

      message.success('Estado del envío actualizado correctamente');

      return response.data;
    } catch (error) {
      console.error('Error updating shipment state:', error);
      message.error('Error al actualizar el estado del envío');
      throw error;
    }
  };

  updateShipmentExternalDocument = async (shipmentId: number, externalDocument: string) => {
    try {
      const response = await this.shipmentApi.updateShipmentExternalDocument(
        shipmentId,
        externalDocument,
      );

      message.success('Documento externo actualizado correctamente');

      return response.data;
    } catch (error) {
      message.error('Error al actualizar el documento externo');
      throw error;
    }
  };

  updateShipment = async (shipmentId: number, shipmentData: Partial<Shipment>) => {
    try {
      const response = await this.shipmentApi.updateShipment(shipmentId, shipmentData);

      message.success('Envío actualizado correctamente');

      return response;
    } catch (error) {
      message.error('Error al actualizar el envío');
      throw error;
    }
  };

  getMessages = async (shipmentId: number) => {
    try {
      const messages: PackageMessage[] = await this.shipmentApi.getMessages(shipmentId);
      const messagesData: PackageMessage[] = messages.map(msg => ({
        ...msg,
        origin_user_data:
          typeof msg.origin_user_data === 'number'
            ? msg.origin_user_data
            : new User(msg.origin_user_data),
      }));

      return messagesData;
    } catch (error) {
      message.error('Error al obtener los mensajes del envío');
      throw error;
    }
  };

  postMessage = async (shipmentId: number, packageMessage: PackageMessage) => {
    try {
      const response = await this.shipmentApi.postMessage(shipmentId, packageMessage);

      return response.data;
    } catch (error) {
      message.error('Error al enviar el mensaje');
      throw error;
    }
  };
}

export default ShipmentRepository;
